import { Fragment } from 'preact';
import { useCallback, useState, useRef } from 'preact/hooks';

import { classNames } from '../utils/UNSAFE_classNames';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { CollapsibleVariantOptions, CollapsibleStyles } from './themes/CollapsibleStyles.css';

import { Flex } from '../UNSAFE_Flex';
import { Separator } from '../UNSAFE_Separator';
import { CollapsibleHeaderIcon } from './CollapsibleHeaderIcon';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { useCollapsiblePress } from './useCollapsiblePress';
import { CollapsibleRedwoodTheme } from './themes/redwood/CollapsibleTheme';

type IntrinsicProps = Pick<HTMLAttributesSignalExcluded<HTMLDivElement>, 'id' | 'children'>;

type HeaderProps = IntrinsicProps & {
  id: string;
  contentId: string;
  isDisabled: boolean;
  isExpanded: boolean;
  iconPosition?: 'start' | 'end';
  variant?: 'basic' | 'horizontal-rule';
  toggleHandler: (target: EventTarget | null) => void;
  accessibleLabel?: string;
  accessibleLabelId?: string;
};

/**
 * Header subcomponent
 */

export const CollapsibleHeader = ({
  children,
  id,
  contentId,
  isDisabled,
  isExpanded,
  iconPosition,
  variant = 'basic',
  toggleHandler,
  accessibleLabel,
  accessibleLabelId
}: HeaderProps) => {
  const [hasFocus, setFocus] = useState<boolean>(false);
  const isMouseDown = useRef<boolean>(false);

  const { classes, styles } = useComponentTheme<CollapsibleVariantOptions, CollapsibleStyles>(
    CollapsibleRedwoodTheme,
    {
      disabled: isDisabled ? 'isDisabled' : 'notDisabled',
      divider: variant === 'horizontal-rule' ? 'hasDivider' : 'noDivider',
      focused: hasFocus ? 'isFocused' : 'notFocused'
    }
  );

  const childrenClasses = classNames([
    styles.headerChildrenStyle,
    iconPosition === 'end' && styles.iconEndStyle,
    iconPosition === 'start' && styles.iconStartStyle
  ]);

  const pressHandler = useCallback(
    (event: Event) => {
      toggleHandler(event.target);
      // if the header has focus highlight and the toggle was caused by a pointer action,
      // remove focus highlight from the header div
      // the test environment may not support PointerEvent, make sure it is supported
      if (hasFocus) {
        const hasPointerEvent = window && 'PointerEvent' in window;
        if ((hasPointerEvent && event instanceof PointerEvent) || event instanceof MouseEvent) {
          setFocus(false);
        }
      }
    },
    [toggleHandler, hasFocus]
  );

  const { pressProps } = useCollapsiblePress(pressHandler, {
    isDisabled
  });

  // if focused, we want to render the focus highlight on the collapsible header div
  const onFocus = useCallback((event: Event) => {
    if (event.type === 'focusin' && !isMouseDown.current) {
      setFocus(true);
    } else {
      setFocus(false);
    }
  }, []);

  const mouseDownHandler = () => {
    isMouseDown.current = true;
  };

  const mouseUpHandler = () => {
    isMouseDown.current = false;
  };

  // Resolve accessible label. By default, the expand button is labelled by the header content.
  // If accessibleLabel or accessibleLabeleId is specified, they get used instead.
  const ariaLabelProp = accessibleLabel
    ? { 'aria-label': accessibleLabel }
    : accessibleLabelId
    ? { 'aria-labelledby': accessibleLabelId }
    : { 'aria-labelledby': id };

  const getHeader = () => (
    <Fragment>
      {iconPosition === 'end' && <div className={childrenClasses}>{children}</div>}
      <div
        tabIndex={0}
        role={'button'}
        aria-controls={contentId}
        aria-expanded={isExpanded}
        onFocus={onFocus}
        onBlur={onFocus}
        onMouseDown={mouseDownHandler}
        onMouseUp={mouseUpHandler}
        className={styles.chevronStyle}
        {...ariaLabelProp}>
        <CollapsibleHeaderIcon
          iconPosition={iconPosition}
          isExpanded={isExpanded}
          isDisabled={isDisabled}
        />
      </div>
      {iconPosition === 'start' && <div className={childrenClasses}>{children}</div>}
    </Fragment>
  );

  return (
    <>
      <div id={id} className={classes} {...pressProps}>
        <Flex align="center" justify={iconPosition === 'start' ? 'start' : 'between'}>
          {getHeader()}
        </Flex>
      </div>
      {variant === 'horizontal-rule' && <Separator />}
    </>
  );
};
